/*
 * AutoAnswer store - for MyCalls and MyCallsCall
 *
 * - Used to share autoAnswer settings between MyCalls and MyCallsCall
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useEmployeesStore } from '@/stores/Employees'

import logger from '@/utilities/Logger'
import globalEvents from '@/utilities/GlobalEvents'

import { MyCallsPhoneType, type MyCallsAutoAnswer } from '@/stores/MyCalls.types'

export const useMyCallsStore = defineStore('myCalls', () => {
	logger.debug('MyCalls: Initializing store')

	const ss = useSettingsStore()
	const employees = useEmployeesStore()

	const autoAnswer = ref<MyCallsAutoAnswer>({ enabled: false })

	/** The type of phone Communicator is paired with */
	const phoneType = computed<MyCallsPhoneType>(() => {
		if (!employees.me) { return MyCallsPhoneType.NONE }
		let type = MyCallsPhoneType.NONE

		for (const ext of employees.me.extensions) {
			if (ext.phones) {
				for (const phone of ext.phones) {
					if (phone._id === ss.realPhoneId) { type = phone.type as MyCallsPhoneType }
				}
			}
		}
		if (ss.settings.softphoneId) { type = MyCallsPhoneType.SOFTPHONE }

		return type
	})

	globalEvents.on('MyCallsAutoAnswerNextRinging', sessionId => {
		logger.debug(`MyCalls: Auto-answering call with sessionId: ${sessionId}`)
		if (phoneType.value === MyCallsPhoneType.SIPPHONE || phoneType.value === MyCallsPhoneType.SOFTPHONE) { // We cannot auto-answer other types of phones
			logger.info(`MyCalls: Told to auto-answer call with sessionId: ${sessionId}`)
			autoAnswer.value.enabled = true
			autoAnswer.value.sessionId = sessionId

			if (autoAnswer.value.timer) { clearTimeout(autoAnswer.value.timer) }

			autoAnswer.value.timer = setTimeout(() => {
				autoAnswer.value.enabled = false
				autoAnswer.value.timer = undefined
			}, 5000)
		}
	})

	function callWasAutoAnswered() {
		autoAnswer.value.enabled = false
		autoAnswer.value.sessionId = undefined
		autoAnswer.value.last = new Date()
		if (autoAnswer.value.timer) { clearTimeout(autoAnswer.value.timer) }
		autoAnswer.value.timer = undefined
	}

	return { autoAnswer, phoneType, callWasAutoAnswered }
})
